import React from 'react';
import { Box, IconButton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { ChartData } from '../types';
import { PRES, TYPE } from '../chrome';
import { COPY } from '../presentationCopy';
import { hexForSettingsColor, settingsColorIdFromHex } from '../presentationSettings';
import { usePresentationSettings } from '../PresentationSettingsContext';
import {
  CHART_MAX_CATEGORIES,
  CHART_MAX_SERIES,
  parseChartNumber,
  resizeChartCategories,
  resizeChartSeries,
} from './chartModel';
import { ChartLegendIcon, ChartValuesIcon, toolbarToggleSx } from './toolbarIcons';
import ColorPopoverButton from './ColorPopoverButton';

interface ChartLookControlsProps {
  chart: ChartData;
  disabled?: boolean;
  onChange: (next: ChartData) => void;
}

export const ChartLookControls: React.FC<ChartLookControlsProps> = ({ chart, disabled, onChange }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.label, color: PRES.textSecondary, mb: 0.75 }}>
      {COPY.chartLook}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <ToggleButtonGroup size="small" sx={toolbarToggleSx}>
        <ToggleButton
          value="legend"
          aria-label={COPY.chartLegend}
          selected={chart.showLegend}
          disabled={disabled}
          onClick={() => onChange({ ...chart, showLegend: !chart.showLegend })}
        >
          <Tooltip title={COPY.chartLegend} arrow>
            <Box component="span" sx={{ display: 'flex' }}><ChartLegendIcon /></Box>
          </Tooltip>
        </ToggleButton>
        <ToggleButton
          value="values"
          aria-label={COPY.chartShowValues}
          selected={chart.showValues}
          disabled={disabled}
          onClick={() => onChange({ ...chart, showValues: !chart.showValues })}
        >
          <Tooltip title={COPY.chartShowValues} arrow>
            <Box component="span" sx={{ display: 'flex' }}><ChartValuesIcon /></Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mr: 0.5 }}>
        {COPY.chartBackground}
      </Typography>
      {([
        { id: 'none' as const, label: COPY.chartBackgroundNone },
        { id: 'white' as const, label: COPY.chartBackgroundWhite },
        { id: 'sand' as const, label: COPY.chartBackgroundSand },
      ]).map((item) => {
        const active = chart.background === item.id;
        return (
          <Tooltip key={item.id} title={item.label} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Box
                component="button"
                type="button"
                disabled={disabled}
                aria-label={`${COPY.chartBackground}: ${item.label}`}
                aria-pressed={active}
                onClick={() => onChange({ ...chart, background: item.id })}
                sx={{
                  width: 26,
                  height: 26,
                  p: 0,
                  borderRadius: 1,
                  border: `1px solid ${active ? PRES.interactive : PRES.borderStrong}`,
                  boxShadow: active ? `0 0 0 1px ${PRES.interactive}` : 'none',
                  bgcolor: item.id === 'none' ? PRES.white : item.id === 'white' ? '#FFFFFF' : '#EDE8DC',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.45 : 1,
                  backgroundImage: item.id === 'none'
                    ? 'linear-gradient(135deg, transparent 45%, #c0392b 47%, #c0392b 53%, transparent 55%)'
                    : undefined,
                }}
              />
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  </Box>
);

interface ChartDataSheetProps {
  chart: ChartData;
  contentLocked?: boolean;
  styleLocked?: boolean;
  onChange: (next: ChartData) => void;
}

const cellSx = {
  minWidth: 0,
  '& .MuiOutlinedInput-root': {
    bgcolor: PRES.white,
    height: 28,
    minWidth: 0,
    fontSize: 12,
    fontFamily: TYPE.ui,
    borderRadius: 0.75,
  },
  '& .MuiOutlinedInput-input': {
    minWidth: 0,
    py: 0.25,
    px: 0.5,
    textOverflow: 'ellipsis',
  },
};

const SheetAction: React.FC<{
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, disabled, onClick, children }) => (
  <Tooltip title={label} arrow>
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        disabled={disabled}
        aria-label={label}
        onClick={onClick}
        sx={{ width: 20, height: 20, p: 0, color: PRES.textSecondary }}
      >
        {children}
      </IconButton>
    </Box>
  </Tooltip>
);

const ChartDataSheet: React.FC<ChartDataSheetProps> = ({
  chart,
  contentLocked,
  styleLocked,
  onChange,
}) => {
  const { settings } = usePresentationSettings();
  const pie = chart.kind === 'pie';
  const series = pie ? chart.series.slice(0, 1) : chart.series;
  const setCategory = (index: number, value: string) => onChange({
    ...chart,
    categories: chart.categories.map((item, itemIndex) => (itemIndex === index ? value : item)),
  });
  const setSeriesName = (index: number, value: string) => onChange({
    ...chart,
    series: chart.series.map((item, itemIndex) => (itemIndex === index ? { ...item, name: value } : item)),
  });
  const setValue = (seriesIndex: number, categoryIndex: number, raw: string) => onChange({
    ...chart,
    series: chart.series.map((item, itemIndex) => (
      itemIndex === seriesIndex
        ? {
          ...item,
          values: item.values.map((value, valueIndex) => (
            valueIndex === categoryIndex ? parseChartNumber(raw) : value
          )),
        }
        : item
    )),
  });
  const setSeriesColor = (index: number, hex: string) => onChange({
    ...chart,
    series: chart.series.map((item, itemIndex) => (
      itemIndex === index ? { ...item, colorId: settingsColorIdFromHex(settings, hex, item.colorId) } : item
    )),
  });
  const setCategoryColor = (index: number, hex: string) => onChange({
    ...chart,
    categoryColorIds: chart.categoryColorIds.map((item, itemIndex) => (
      itemIndex === index ? settingsColorIdFromHex(settings, hex, item) : item
    )),
  });
  const labelWidth = pie ? 24 : 60;
  const categoryWidth = pie ? 48 : 40;
  const actionWidth = 24;
  const sheetWidth = labelWidth + chart.categories.length * categoryWidth + actionWidth;

  return (
    <Box>
      <Typography sx={{ fontFamily: TYPE.ui, ...TYPE.label, color: PRES.textSecondary, mb: 0.75 }}>
        {COPY.chartData}
      </Typography>
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          border: `1px solid ${PRES.line}`,
          borderRadius: 1,
          bgcolor: PRES.surface,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 5 },
          '&::-webkit-scrollbar-thumb': { bgcolor: PRES.borderStrong, borderRadius: 99 },
        }}
      >
        <Box
          component="table"
          sx={{
            width: sheetWidth <= 244 ? '100%' : sheetWidth,
            minWidth: sheetWidth,
            tableLayout: 'fixed',
            borderCollapse: 'collapse',
          }}
        >
          <Box component="colgroup">
            <Box component="col" style={{ width: labelWidth }} />
            {chart.categories.map((_label, index) => (
              <Box component="col" key={`col-${index}`} style={{ width: categoryWidth }} />
            ))}
            <Box component="col" style={{ width: actionWidth }} />
          </Box>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={{ width: labelWidth, p: 0.25 }} />
              {chart.categories.map((label, categoryIndex) => (
                <Box component="th" key={`cat-${categoryIndex}`} sx={{ width: categoryWidth, p: 0.25 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                    {pie && (
                      <ColorPopoverButton
                        label={`${COPY.chartCategory} ${categoryIndex + 1}`}
                        color={hexForSettingsColor(settings, chart.categoryColorIds[categoryIndex])}
                        disabled={styleLocked}
                        size={12}
                        onChange={(hex) => setCategoryColor(categoryIndex, hex)}
                      />
                    )}
                    <TextField
                      size="small"
                      fullWidth
                      value={label}
                      disabled={contentLocked}
                      onChange={(event) => setCategory(categoryIndex, event.target.value)}
                      inputProps={{ 'aria-label': `${COPY.chartCategory} ${categoryIndex + 1}` }}
                      sx={cellSx}
                    />
                  </Box>
                </Box>
              ))}
              <Box component="th" sx={{ p: 0.1, width: actionWidth }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <SheetAction
                    label={COPY.chartAddCategory}
                    disabled={contentLocked || chart.categories.length >= CHART_MAX_CATEGORIES}
                    onClick={() => onChange(resizeChartCategories(chart, chart.categories.length + 1))}
                  >
                    <AddIcon sx={{ fontSize: 15 }} />
                  </SheetAction>
                  <SheetAction
                    label={COPY.chartRemoveCategory}
                    disabled={contentLocked || chart.categories.length <= 1}
                    onClick={() => onChange(resizeChartCategories(chart, chart.categories.length - 1))}
                  >
                    <RemoveIcon sx={{ fontSize: 15 }} />
                  </SheetAction>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box component="tbody">
            {series.map((item, seriesIndex) => (
              <Box component="tr" key={item.id}>
                <Box component="td" sx={{ p: 0.25, width: labelWidth }}>
                  {!pie && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                      <ColorPopoverButton
                        label={`${COPY.chartSeries} ${seriesIndex + 1}`}
                        color={hexForSettingsColor(settings, item.colorId)}
                        disabled={styleLocked}
                        size={12}
                        onChange={(hex) => setSeriesColor(seriesIndex, hex)}
                      />
                      <TextField
                        size="small"
                        fullWidth
                        value={item.name}
                        disabled={contentLocked}
                        onChange={(event) => setSeriesName(seriesIndex, event.target.value)}
                        inputProps={{ 'aria-label': `${COPY.chartSeries} ${seriesIndex + 1}` }}
                        sx={cellSx}
                      />
                    </Box>
                  )}
                </Box>
                {chart.categories.map((category, categoryIndex) => (
                  <Box component="td" key={`${item.id}-${categoryIndex}`} sx={{ p: 0.25, width: categoryWidth }}>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.values[categoryIndex] ?? 0}
                      disabled={contentLocked}
                      onChange={(event) => setValue(seriesIndex, categoryIndex, event.target.value)}
                      inputProps={{ 'aria-label': `${item.name} ${category}` }}
                      sx={cellSx}
                    />
                  </Box>
                ))}
                <Box component="td" />
              </Box>
            ))}
            {!pie && (
              <Box component="tr">
                <Box component="td" sx={{ p: 0.2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                    <SheetAction
                      label={COPY.chartAddSeries}
                      disabled={contentLocked || chart.series.length >= CHART_MAX_SERIES}
                      onClick={() => onChange(resizeChartSeries(chart, chart.series.length + 1))}
                    >
                      <AddIcon sx={{ fontSize: 15 }} />
                    </SheetAction>
                    <SheetAction
                      label={COPY.chartRemoveSeries}
                      disabled={contentLocked || chart.series.length <= 1}
                      onClick={() => onChange(resizeChartSeries(chart, chart.series.length - 1))}
                    >
                      <RemoveIcon sx={{ fontSize: 15 }} />
                    </SheetAction>
                  </Box>
                </Box>
                <Box component="td" colSpan={chart.categories.length + 1} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Typography sx={{ mt: 0.75, fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary }}>
        {COPY.chartPasteHint}
      </Typography>
    </Box>
  );
};

export default ChartDataSheet;
